import { useEffect, useRef } from 'react';
import axios from 'axios';

import type { SessionActivityResponse } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import {
  ACTIVITY_REPORT_INTERVAL_MS,
  getLastActivityReportAt,
  parsePositiveNumber,
  PRESENCE_EVENTS,
  PRESENCE_SIGNAL_EVENT,
  publishSessionExpiresAt,
  restoreLastActivityReportAt,
  SESSION_EXPIRES_AT_KEY,
  setLastActivityReportAt,
} from './userPresence';

interface UseUserPresenceReportingParams {
  isEnabled: boolean;
  isPaused: boolean;
  isCallInProgress: boolean;
  onSessionRefreshed: (newExpiresAt: number | null) => void;
}

// A call tick landing a moment before a full minute after the last report, from timer drift or another
// tab's report, would otherwise be skipped, and the next chance comes a minute later.
const REPORT_TIMER_TOLERANCE_MS = 2000;

// Apart from login, the server's idle clock moves only on a POST to /users/activity: every other request
// the app sends, a poll, a token refresh or a dialog's tracking, is not evidence that a person is there.
const useUserPresenceReporting = ({
  isEnabled,
  isPaused,
  isCallInProgress,
  onSessionRefreshed,
}: UseUserPresenceReportingParams): void => {
  const onSessionRefreshedRef = useRef(onSessionRefreshed);
  // This tab's own throttle, so a tab whose storage is unavailable still reports at most once a minute.
  const lastReportInThisTabRef = useRef<number | null>(null);
  useEffect(() => {
    onSessionRefreshedRef.current = onSessionRefreshed;
  }, [onSessionRefreshed]);

  useEffect(() => {
    if (!isEnabled || isPaused) {
      return;
    }

    let isReportInFlight = false;

    const reportPresence = async () => {
      const now = Date.now();
      const lastReportAt = Math.max(getLastActivityReportAt() ?? 0, lastReportInThisTabRef.current ?? 0);
      if (isReportInFlight || now - lastReportAt < ACTIVITY_REPORT_INTERVAL_MS - REPORT_TIMER_TOLERANCE_MS) {
        return;
      }

      isReportInFlight = true;
      // Recorded before the request, so a failing endpoint is retried from this tab once a minute, not
      // on every event. The shared time is handed back on failure, so other tabs are not held up by it.
      const previousSharedReportAt = getLastActivityReportAt();
      lastReportInThisTabRef.current = now;
      setLastActivityReportAt(now);
      try {
        const response = await axios.post<SessionActivityResponse>('/api/v1/users/activity');
        const newExpiresAt = response.data?.session_expires_at ?? null;
        if (newExpiresAt) {
          publishSessionExpiresAt(newExpiresAt);
        }
        onSessionRefreshedRef.current(newExpiresAt);
      } catch (error) {
        // A 401 is a session that has already expired; the session check owns that outcome.
        if (!(axios.isAxiosError(error) && error.response?.status === 401)) {
          restoreLastActivityReportAt(now, previousSharedReportAt);
          await reportAxiosError(error, false);
        }
      } finally {
        isReportInFlight = false;
      }
    };

    const presenceEvents = [...PRESENCE_EVENTS, PRESENCE_SIGNAL_EVENT];
    presenceEvents.forEach((eventName) =>
      window.addEventListener(eventName, reportPresence, { capture: true, passive: true })
    );
    // A call can run for an hour with no input, and the adjuster on it is present throughout.
    const callInterval = isCallInProgress ? setInterval(reportPresence, ACTIVITY_REPORT_INTERVAL_MS) : null;

    return () => {
      presenceEvents.forEach((eventName) => window.removeEventListener(eventName, reportPresence, { capture: true }));
      if (callInterval) {
        clearInterval(callInterval);
      }
    };
  }, [isEnabled, isPaused, isCallInProgress]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const followOtherTabs = (event: StorageEvent) => {
      if (event.key !== SESSION_EXPIRES_AT_KEY) {
        return;
      }
      const newExpiresAt = parsePositiveNumber(event.newValue);
      if (newExpiresAt) {
        onSessionRefreshedRef.current(newExpiresAt);
      }
    };

    window.addEventListener('storage', followOtherTabs);
    return () => window.removeEventListener('storage', followOtherTabs);
  }, [isEnabled]);
};

export default useUserPresenceReporting;
